import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { listTemplates, listCategoryTemplates } from "../utils/api";
import { useAuthContext } from "../contexts/AuthContext";
import Lottie from "lottie-react";
import loadingAnimation from "../assets/lottie/loading.json";

const TEMPLATES_PER_PAGE = 9; // 9 templates + 1 "Create with AI" card = 10 items per page

// Check if a template filename is a "favorite" (should stay at top, not shuffled)
function isFavoriteTemplate(filename) {
  const favoritePrefix = ['0_fav_', '0_0-', '0_1-', '0_2-'];
  return favoritePrefix.some(prefix => filename.startsWith(prefix));
}

// Fisher-Yates shuffle algorithm to randomize template order
// Favorites (0_fav_, 0_0-, 0_1-, 0_2- prefixed) stay at top in original order
function shuffleArray(array) {
  // Separate favorites from regular templates
  const favorites = array.filter(isFavoriteTemplate);
  const regular = array.filter(f => !isFavoriteTemplate(f));
  
  // Only shuffle regular templates
  const shuffled = [...regular];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  // Return favorites first (in original order), then shuffled regular templates
  return [...favorites, ...shuffled];
}

// Lottie loading spinner component
function LoadingSpinner({ size = "md" }) {
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-12 h-12",
    lg: "w-16 h-16"
  };
  
  return (
    <div className={sizeClasses[size]}>
      <Lottie 
        animationData={loadingAnimation} 
        loop={true}
        autoplay={true}
      />
    </div>
  );
}

// Lazy loading image component that only loads when visible
function LazyImage({ src, alt, className, onClick }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect(); // Stop observing once in view
        }
      },
      {
        rootMargin: '100px', // Start loading 100px before entering viewport
        threshold: 0.01
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={imgRef} className={`${className} bg-gray-100`}>
      {isInView ? (
        <img
          src={src}
          alt={alt}
          className={`w-full h-full object-contain transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setIsLoaded(true)}
          onClick={onClick}
        />
      ) : (
        // Placeholder while not in view
        <div className="w-full h-full flex items-center justify-center">
          <div className="w-8 h-8 rounded-full border-2 border-gray-200 border-t-indigo-500 animate-spin" />
        </div>
      )}
      {isInView && !isLoaded && (
        // Loading spinner while image is loading
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="w-8 h-8 rounded-full border-2 border-gray-200 border-t-indigo-500 animate-spin" />
        </div>
      )}
    </div>
  );
}

// Video thumbnail component for video templates - auto-plays when visible
// Safari-optimized: uses #t=0.001 hack, preload="auto", and canplay event for faster loading
function LazyVideoThumbnail({ src, alt, className, onClick }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const containerRef = useRef(null);
  const videoRef = useRef(null);

  // Safari-optimized: append #t=0.001 to show first frame immediately
  // This tricks Safari into loading the first frame as a "poster"
  const videoSrc = src ? `${src}#t=0.001` : src;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      {
        rootMargin: '50px',
        threshold: 0.3 // 30% visible before playing
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-play video when in view, pause when out of view
  useEffect(() => {
    if (videoRef.current && isLoaded) {
      if (isInView) {
        // Small delay before playing to avoid too many videos playing at once
        const timer = setTimeout(() => {
          videoRef.current?.play().catch(() => {});
          setIsPlaying(true);
        }, 200);
        return () => clearTimeout(timer);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  }, [isInView, isLoaded]);

  // Handle video ready - use multiple events for cross-browser support
  const handleVideoReady = useCallback(() => {
    if (!isLoaded) {
      setIsLoaded(true);
    }
  }, [isLoaded]);

  return (
    <div 
      ref={containerRef} 
      className={`${className} bg-gray-900 relative`}
    >
      {isInView ? (
        <>
          <video
            ref={videoRef}
            src={videoSrc}
            muted
            loop
            playsInline
            // Safari-specific: webkit-playsinline for older Safari versions
            webkit-playsinline="true"
            // Safari-optimized: "auto" loads faster than "metadata" on Safari
            preload="auto"
            // Required for CORS videos (Firebase storage)
            crossOrigin="anonymous"
            className={`w-full h-full object-cover transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
            // Use multiple events for better cross-browser support
            // Safari: canplay fires faster than loadeddata
            onCanPlay={handleVideoReady}
            onLoadedData={handleVideoReady}
            onClick={onClick}
          />
          {/* Video indicator badge */}
          <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 z-10">
            <svg className={`w-3 h-3 ${isPlaying ? 'animate-pulse' : ''}`} fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z"/>
            </svg>
            <span>Video</span>
          </div>
          {/* Loading/buffering indicator */}
          {isLoaded && !isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/20">
              <div className="w-10 h-10 rounded-full bg-black/60 flex items-center justify-center">
                <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
            </div>
          )}
        </>
      ) : (
        // Placeholder when not in view - show static preview
        <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
          <div className="text-center">
            <svg className="w-10 h-10 text-gray-600 mx-auto mb-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z"/>
            </svg>
            <span className="text-xs text-gray-500">Video</span>
          </div>
        </div>
      )}
      {isInView && !isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
          <div className="w-8 h-8 rounded-full border-2 border-gray-700 border-t-indigo-500 animate-spin" />
        </div>
      )}
    </div>
  );
}

// Special "Create with AI" card component
function CreateWithAICard({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="group relative overflow-hidden rounded-lg sm:rounded-xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 shadow transition hover:shadow-xl aspect-[3/4] border border-slate-700 hover:border-indigo-500/50"
    >
      {/* Subtle grid pattern overlay */}
      <div className="absolute inset-0 opacity-10">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-slate-400"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>
      
      {/* Gradient glow effect on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/0 via-purple-600/0 to-pink-600/0 group-hover:from-indigo-600/20 group-hover:via-purple-600/20 group-hover:to-pink-600/20 transition-all duration-500" />
      
      {/* Content */}
      <div className="relative h-full flex flex-col items-center justify-center p-4 sm:p-6 text-center">
        {/* AI Icon */}
        <div className="mb-3 sm:mb-4 w-12 h-12 sm:w-16 sm:h-16 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
          <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
          </svg>
        </div>
        
        {/* Text */}
        <p className="text-white font-semibold text-sm sm:text-base mb-1 sm:mb-2">
          Don't see what you want?
        </p>
        <p className="text-slate-400 text-xs sm:text-sm mb-3 sm:mb-4 leading-relaxed">
          Create your own unique design with AI
        </p>
        
        {/* CTA Button */}
        <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full text-white text-xs sm:text-sm font-medium group-hover:from-indigo-400 group-hover:to-purple-500 transition-all shadow-md">
          Create with AI ✨
        </div>
      </div>
    </button>
  );
}

export default function TemplateGrid({ onPick, onCreateWithAI, pendingTemplate, pendingCategory, onCategoryChange, getShareLink, getCategoryShareLink }) {
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState("");
  const [copiedLink, setCopiedLink] = useState(null); // Track which template link was just copied
  const [copiedCategoryLink, setCopiedCategoryLink] = useState(null); // Track which category link was just copied
  const [categoriesLoading, setCategoriesLoading] = useState(true); // Track initial categories loading
  
  // Media type filter: "all" | "image" | "video"
  const [mediaFilter, setMediaFilter] = useState("all");
  const [mediaCounts, setMediaCounts] = useState({}); // { category: { images: n, videos: n } }
  
  // Pagination state per category (keyed by category + filter)
  const [templatesByCategory, setTemplatesByCategory] = useState({});
  const [templatesInfoByCategory, setTemplatesInfoByCategory] = useState({}); // Store media type info
  const [paginationState, setPaginationState] = useState({}); // { category: { page, hasMore, total, loading } }

  // Fetch initial category list on mount
  useEffect(() => {
    async function fetchCategories() {
      setCategoriesLoading(true);
      try {
        const { data } = await listTemplates();
        let cats = Object.keys(data);
        
        // Prioritize Mother's Day first, then Valentine's / winter holidays
        const valentinesKeywords = ['valentine', 'valentines'];
        const winterHolidays = ['christmas', 'hanukkah', 'new year', 'new years', 'newyear', 'newyears'];

        const isMothersDayCategory = (cat) => {
          const lower = cat.toLowerCase();
          const collapsed = lower.replace(/[^a-z]/g, '');
          if (lower.includes('grandmother')) return false;
          return (
            (lower.includes('mother') && lower.includes('day') && !lower.includes('father')) ||
            collapsed.includes('mothersday')
          );
        };

        const mothersDay = [];
        const valentines = [];
        const winterPrioritized = [];
        const remaining = [];

        cats.forEach((cat) => {
          const catLower = cat.toLowerCase().replace(/[^a-z]/g, '');
          if (isMothersDayCategory(cat)) {
            mothersDay.push(cat);
          } else if (valentinesKeywords.some((v) => cat.toLowerCase().includes(v) || catLower.includes(v.replace(/[^a-z]/g, '')))) {
            valentines.push(cat);
          } else if (winterHolidays.some((h) => cat.toLowerCase().includes(h) || catLower.includes(h.replace(/[^a-z]/g, '')))) {
            winterPrioritized.push(cat);
          } else {
            remaining.push(cat);
          }
        });

        cats = [...mothersDay, ...valentines, ...winterPrioritized, ...remaining];
        
        setCategories(cats);
        // Initialize pagination state for each category
        const initialPagination = {};
        cats.forEach(cat => {
          initialPagination[cat] = { page: 0, hasMore: true, total: data[cat]?.length || 0, loading: false };
        });
        setPaginationState(initialPagination);
        if (cats.length) setActiveTab(cats[0]);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      } finally {
        setCategoriesLoading(false);
      }
    }
    fetchCategories();
  }, []);

  // Create a composite key for caching templates by category AND filter
  const getCacheKey = (category, filter) => `${category}__${filter}`;
  
  // Load templates for a category (paginated) with media filter support
  const loadTemplates = useCallback(async (category, isLoadMore = false, filter = mediaFilter) => {
    const cacheKey = getCacheKey(category, filter);
    const currentState = paginationState[cacheKey] || { page: 0, hasMore: true, loading: false };
    
    if (currentState.loading || (!isLoadMore && currentState.page > 0)) return;
    
    // Set loading state
    setPaginationState(prev => ({
      ...prev,
      [cacheKey]: { ...prev[cacheKey], loading: true }
    }));

    try {
      const nextPage = isLoadMore ? currentState.page + 1 : 1;
      const { data } = await listCategoryTemplates(category, nextPage, TEMPLATES_PER_PAGE, filter);
      
      setTemplatesByCategory(prev => ({
        ...prev,
        [cacheKey]: isLoadMore 
          ? [...(prev[cacheKey] || []), ...data.templates]
          : shuffleArray(data.templates) // Shuffle on initial load for variety
      }));
      
      // Store templates_info for media type detection (video vs image)
      if (data.templates_info) {
        setTemplatesInfoByCategory(prev => {
          const existingInfo = prev[category] || {};
          const newInfo = { ...existingInfo };
          data.templates_info.forEach(info => {
            newInfo[info.filename] = info;
          });
          return { ...prev, [category]: newInfo };
        });
      }
      
      // Store media counts for filter badges
      setMediaCounts(prev => ({
        ...prev,
        [category]: {
          images: data.total_images || 0,
          videos: data.total_videos || 0
        }
      }));
      
      setPaginationState(prev => ({
        ...prev,
        [cacheKey]: {
          page: data.page,
          hasMore: data.has_more,
          total: data.total,
          loading: false
        }
      }));
    } catch (error) {
      console.error("Failed to load templates:", error);
      setPaginationState(prev => ({
        ...prev,
        [cacheKey]: { ...prev[cacheKey], loading: false }
      }));
    }
  }, [paginationState, mediaFilter]);

  // Load first batch when tab or filter changes
  useEffect(() => {
    if (activeTab) {
      const cacheKey = getCacheKey(activeTab, mediaFilter);
      if (!templatesByCategory[cacheKey] || templatesByCategory[cacheKey].length === 0) {
        loadTemplates(activeTab, false, mediaFilter);
      }
    }
  }, [activeTab, mediaFilter, templatesByCategory, loadTemplates]);

  // Handle pending category from URL deep link (just category, no specific template)
  useEffect(() => {
    if (pendingCategory && categories.length > 0) {
      // Find matching category (case insensitive)
      const matchingCategory = categories.find(
        cat => cat.toLowerCase() === pendingCategory.toLowerCase()
      );
      if (matchingCategory) {
        // Set the active tab to the matching category
        if (matchingCategory !== activeTab) {
          setActiveTab(matchingCategory);
        }
        // Only clear after successfully finding and setting the category
        if (onCategoryChange) {
          onCategoryChange();
        }
      }
    }
  }, [pendingCategory, categories, activeTab, onCategoryChange]);

  // Handle pending template from URL deep link
  useEffect(() => {
    if (pendingTemplate && categories.length > 0) {
      const { category } = pendingTemplate;
      // Find matching category (case insensitive)
      const matchingCategory = categories.find(
        cat => cat.toLowerCase() === category.toLowerCase()
      );
      if (matchingCategory && matchingCategory !== activeTab) {
        setActiveTab(matchingCategory);
      }
    }
  }, [pendingTemplate, categories, activeTab]);

  // Auto-select template if coming from deep link
  useEffect(() => {
    if (pendingTemplate && templatesByCategory[activeTab]?.length > 0) {
      const { filename, templateId, category } = pendingTemplate;
      
      // Check if this is the right category
      if (activeTab.toLowerCase() !== category.toLowerCase()) return;

      // Find the matching template
      let matchedFilename = null;
      if (filename) {
        matchedFilename = templatesByCategory[activeTab].find(f => 
          f.toLowerCase() === filename.toLowerCase()
        );
      }
      if (!matchedFilename && templateId) {
        matchedFilename = templatesByCategory[activeTab].find(f => 
          f.replace(/[^0-9]/g, '') === templateId
        );
      }

      if (matchedFilename) {
        // Auto-trigger template selection
        console.log("[TemplateGrid] Auto-selecting template from deep link:", matchedFilename);
        handlePick(activeTab, matchedFilename);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingTemplate, templatesByCategory, activeTab]);

  // Handle copying share link for templates
  const handleShareClick = async (e, category, filename) => {
    e.stopPropagation(); // Prevent template selection
    
    if (!getShareLink) return;
    
    const template = {
      category,
      filename,
      id: filename.replace(/[^0-9]/g, '')
    };
    
    const shareLink = getShareLink(template);
    
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopiedLink(`${category}-${filename}`);
      // Clear the "copied" state after 2 seconds
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
      // Fallback: show alert with link
      alert(`Share link: ${shareLink}`);
    }
  };

  // Handle copying share link for categories
  const handleCategoryShareClick = async (e, category) => {
    e.stopPropagation(); // Prevent tab switching
    
    if (!getCategoryShareLink) return;
    
    const shareLink = getCategoryShareLink(category);
    
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopiedCategoryLink(category);
      // Clear the "copied" state after 2 seconds
      setTimeout(() => setCopiedCategoryLink(null), 2000);
    } catch (err) {
      console.error("Failed to copy category link:", err);
      // Fallback: show alert with link
      alert(`Category link: ${shareLink}`);
    }
  };

  const handleLoadMore = () => {
    loadTemplates(activeTab, true, mediaFilter);
  };

  // helper to build a URL you can feed directly to <img> or <video>
  // For Firebase videos, use the direct URL; for local images, build the API URL
  const makeSrc = (category, filename) => {
    const info = templatesInfoByCategory[category]?.[filename];
    // If it's a Firebase video with a direct URL, use that
    if (info?.url && info?.source === "firebase") {
      return info.url;
    }
    // Otherwise, build the local API URL
    return `${process.env.REACT_APP_API_URL || "http://192.168.1.165:8000"}/templates/${category}/${filename}`;
  };

  // Helper to check if a template is a video
  const isVideoTemplate = (category, filename) => {
    // First check templates_info from API
    const info = templatesInfoByCategory[category]?.[filename];
    if (info) return info.is_video || info.media_type === "video";
    
    // Fallback: check file extension
    const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv'];
    return videoExtensions.some(ext => filename.toLowerCase().endsWith(ext));
  };

  // Helper to get template info (including Firebase URL if available)
  const getTemplateInfo = (category, filename) => {
    return templatesInfoByCategory[category]?.[filename] || null;
  };

  const handlePick = async (category, filename) => {
    // Extract template ID from filename (e.g. "graduation1.png" -> "1")
    const templateId = filename.replace(/[^0-9]/g, '') || filename.replace(/\.[^/.]+$/, '');
    
    // Get template info (includes Firebase URL for videos)
    const templateInfo = getTemplateInfo(category, filename);
    
    // Get the direct template URL (Firebase for videos, local API for images)
    const src = makeSrc(category, filename);
    
    // Determine if this is a video template
    const isVideo = isVideoTemplate(category, filename);
    const mediaType = isVideo ? "video" : "image";
    const source = templateInfo?.source || "local";
    
    // Get dimensions - for video we'll use default, for images we load them
    let cardSize = "1024x1024"; // default
    
    if (!isVideo) {
      // Load the image to get its actual dimensions
      const getImageDimensions = (url) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve({ width: img.width, height: img.height });
          img.onerror = () => resolve({ width: 1024, height: 1024 }); // fallback
          img.src = url;
        });
      };
      
      const dimensions = await getImageDimensions(src);
      cardSize = `${dimensions.width}x${dimensions.height}`;
    } else {
      // For video, try to get dimensions from video element with timeout
      const getVideoDimensions = (url) => {
        return new Promise((resolve) => {
          const video = document.createElement('video');
          video.preload = 'metadata';
          video.crossOrigin = 'anonymous'; // Enable CORS
          
          // Timeout after 3 seconds to prevent hanging
          const timeout = setTimeout(() => {
            console.log('[TemplateGrid] Video dimension load timed out, using fallback dimensions');
            resolve({ width: 1280, height: 720 }); // fallback 16:9
          }, 3000);
          
          video.onloadedmetadata = () => {
            clearTimeout(timeout);
            resolve({ width: video.videoWidth || 1280, height: video.videoHeight || 720 });
          };
          video.onerror = () => {
            clearTimeout(timeout);
            console.log('[TemplateGrid] Video dimension load error, using fallback dimensions');
            resolve({ width: 1280, height: 720 }); // fallback 16:9
          };
          video.src = url;
        });
      };
      
      const dimensions = await getVideoDimensions(src);
      cardSize = `${dimensions.width}x${dimensions.height}`;
    }
    
    // If onPick is provided, use it (for Hero page integration)
    if (onPick) {
      onPick({
        id: templateId,
        category: category,
        src: src,
        filename: filename,
        cardSize: cardSize,
        mediaType: mediaType,
        isVideo: isVideo,
        source: source,  // "local" or "firebase"
        firebaseUrl: templateInfo?.url || null  // Direct Firebase URL for videos
      });
      return;
    }
    
    // Otherwise use direct navigation (fallback)
    navigate(`/edit/${category}/${templateId}`, { 
      state: { 
        src,
        category,
        cardSize: cardSize,
        mediaType: mediaType,
        isVideo: isVideo,
        source: source,
        firebaseUrl: templateInfo?.url || null
      } 
    });
  };

  return (
    <div className="mx-auto max-w-[95vw] xl:max-w-[95vw] px-2 sm:px-4 pb-12 sm:pb-20 md:pb-24">
      <div className="text-center mb-6 sm:mb-8">
        <h2 className="mb-2 sm:mb-3 text-xl sm:text-2xl font-semibold px-4">
          Choose from Beautiful Templates
        </h2>
        <p className="text-gray-600 text-sm sm:text-base max-w-2xl mx-auto px-4">
          Start with professionally designed templates and customize them to make them your own. 
          Perfect for when you want to create something beautiful quickly.
        </p>
        {!user && (
          <button
            onClick={() => navigate('/subscribe')}
            className="mt-4 group inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
          >
            <span className="text-lg mr-2">🎁</span>
            <span className="font-semibold">Start with a 3-day free trial</span>
            <svg className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      {/* category tabs */}
      {categoriesLoading ? (
        <div className="mb-6 sm:mb-8 flex justify-center items-center py-4 border-b border-gray-200 px-2">
          <div className="flex items-center gap-3 text-gray-500">
            <LoadingSpinner size="sm" />
            <span className="text-sm sm:text-base">Loading categories...</span>
          </div>
        </div>
      ) : (
        <div className="mb-6 sm:mb-8 flex flex-wrap justify-center gap-2 sm:gap-4 border-b border-gray-200 px-2">
          {categories.map((cat) => {
            const catLower = cat.toLowerCase();
            const isChristmas = catLower === 'christmas';
            const isHanukkah = catLower === 'hanukkah' || catLower === 'chanukah';
            const isNewYear = catLower.includes('new year') || catLower.includes('newyear');
            const isMothersDayTab =
              (catLower.includes('mother') && catLower.includes('day') && !catLower.includes('father') && !catLower.includes('grandmother')) ||
              catLower.replace(/[^a-z]/g, '').includes('mothersday');
            const isFeaturedHoliday = isChristmas || isHanukkah || isNewYear || isMothersDayTab;
            const isActive = activeTab === cat;
            const isCategoryCopied = copiedCategoryLink === cat;
            
            // Get festive config for featured holidays
            const getFestiveConfig = () => {
              if (isMothersDayTab)
                return {
                  emoji: '🌷',
                  badge: 'Celebrate Mom!',
                  colors: { active: 'border-violet-500 text-violet-700', inactive: 'text-violet-500 hover:text-violet-700' },
                  gradient: 'from-violet-500 to-fuchsia-300',
                };
              if (isChristmas) return { emoji: '🎄', badge: "'Tis the Season!", colors: { active: 'border-red-600 text-red-600', inactive: 'text-green-700 hover:text-red-600' }, gradient: 'from-red-500 to-green-600' };
              if (isHanukkah) return { emoji: '🕎', badge: 'Festival of Lights!', colors: { active: 'border-blue-600 text-blue-600', inactive: 'text-blue-700 hover:text-blue-500' }, gradient: 'from-blue-500 to-blue-300' };
              if (isNewYear) return { emoji: '🎆', badge: 'Cheers to 2026!', colors: { active: 'border-amber-500 text-amber-600', inactive: 'text-amber-600 hover:text-amber-500' }, gradient: 'from-amber-400 to-yellow-300' };
              return null;
            };
            
            const festive = getFestiveConfig();
            
            return (
              <div key={cat} className="relative group/tab flex items-center">
                <button
                  onClick={() => {
                    setActiveTab(cat);
                    // Reset filter to "all" when switching categories
                    // (in case new category doesn't have videos)
                    setMediaFilter("all");
                  }}
                  className={`pb-2 text-sm sm:text-base md:text-lg font-medium transition focus:outline-none whitespace-nowrap flex items-center gap-1.5 ${
                    isFeaturedHoliday
                      ? isActive
                        ? `border-b-2 ${festive.colors.active}`
                        : festive.colors.inactive
                      : isActive
                        ? "border-b-2 border-indigo-600 text-indigo-600"
                        : "text-gray-600 hover:text-indigo-600"
                  }`}
                >
                  {festive && (
                    <span className="text-base sm:text-lg">{festive.emoji}</span>
                  )}
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  {festive && isActive && (
                    <span className={`ml-1 px-1.5 py-0.5 text-[10px] sm:text-xs bg-gradient-to-r ${festive.gradient} text-white rounded-full font-semibold`}>
                      {festive.badge}
                    </span>
                  )}
                </button>
                
                {/* Category share button - shows on hover for all tabs */}
                {getCategoryShareLink && (
                  <button
                    onClick={(e) => handleCategoryShareClick(e, cat)}
                    className={`ml-1 p-1 rounded-full transition-all duration-200 ${
                      isCategoryCopied 
                        ? 'bg-green-500 text-white' 
                        : isActive
                          ? 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'
                          : 'text-gray-300 hover:text-indigo-600 hover:bg-indigo-50 opacity-0 group-hover/tab:opacity-100'
                    }`}
                    title={isCategoryCopied ? "Link copied!" : "Copy link to this category"}
                  >
                    {isCategoryCopied ? (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Media type filter buttons - only show if there are both images AND videos */}
      {activeTab && mediaCounts[activeTab]?.videos > 0 && mediaCounts[activeTab]?.images > 0 && (
        <div className="mb-4 flex justify-center gap-2">
          <button
            onClick={() => setMediaFilter("all")}
            className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
              mediaFilter === "all"
                ? "bg-indigo-600 text-white shadow-md"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            All Templates
            <span className="ml-1.5 text-xs opacity-75">
              ({(mediaCounts[activeTab]?.images || 0) + (mediaCounts[activeTab]?.videos || 0)})
            </span>
          </button>
          <button
            onClick={() => setMediaFilter("image")}
            className={`px-4 py-2 text-sm font-medium rounded-full transition-all flex items-center gap-1.5 ${
              mediaFilter === "image"
                ? "bg-indigo-600 text-white shadow-md"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Images
            <span className="text-xs opacity-75">({mediaCounts[activeTab].images})</span>
          </button>
          <button
            onClick={() => setMediaFilter("video")}
            className={`px-4 py-2 text-sm font-medium rounded-full transition-all flex items-center gap-1.5 ${
              mediaFilter === "video"
                ? "bg-indigo-600 text-white shadow-md"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z"/>
            </svg>
            Videos
            <span className="text-xs opacity-75">({mediaCounts[activeTab].videos})</span>
          </button>
        </div>
      )}

      {/* template grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 md:gap-6">
        {/* Show skeleton loading cards when templates are loading for active tab */}
        {activeTab && paginationState[getCacheKey(activeTab, mediaFilter)]?.loading && !templatesByCategory[getCacheKey(activeTab, mediaFilter)]?.length && (
          [...Array(8)].map((_, idx) => (
            <div
              key={`skeleton-${idx}`}
              className="relative overflow-hidden rounded-lg sm:rounded-xl bg-gray-50 shadow aspect-[3/4]"
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <LoadingSpinner size="md" />
              </div>
            </div>
          ))
        )}
        
        {templatesByCategory[getCacheKey(activeTab, mediaFilter)]?.map((filename, idx) => {
          const isCopied = copiedLink === `${activeTab}-${filename}`;
          const isVideo = isVideoTemplate(activeTab, filename);
          
          return (
            <div
              key={`${activeTab}-${filename}-${idx}`}
              className={`group relative overflow-hidden rounded-lg sm:rounded-xl shadow transition hover:shadow-lg aspect-[3/4] ${
                isVideo ? 'bg-gray-900' : 'bg-white'
              }`}
            >
              <button
                onClick={() => handlePick(activeTab, filename)}
                className="w-full h-full"
              >
                {isVideo ? (
                  /* Video template thumbnail */
                  <LazyVideoThumbnail
                    src={makeSrc(activeTab, filename)}
                    alt={filename}
                    className="w-full h-full transition-transform duration-300 group-hover:scale-105"
                  />
                ) : (
                  /* Image template thumbnail */
                  <LazyImage
                    src={makeSrc(activeTab, filename)}
                    alt={filename}
                    className="w-full h-full transition-transform duration-300 group-hover:scale-105"
                  />
                )}
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 pointer-events-none">
                  <span className="text-xs sm:text-sm font-medium text-white px-2 text-center">
                    {isVideo ? "Use this video template" : "Customize this template"}
                  </span>
                </div>
              </button>
              
              {/* Share button */}
              {getShareLink && (
                <button
                  onClick={(e) => handleShareClick(e, activeTab, filename)}
                  className={`absolute top-2 right-2 p-2 rounded-full transition-all duration-200 z-10 ${
                    isCopied 
                      ? 'bg-green-500 text-white' 
                      : 'bg-white/90 text-gray-600 hover:bg-white hover:text-indigo-600 opacity-0 group-hover:opacity-100'
                  } shadow-md`}
                  title={isCopied ? "Link copied!" : "Copy share link"}
                >
                  {isCopied ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          );
        })}
        
        {/* Create with AI card - shown as an extra item after at least 2 templates are loaded */}
        {templatesByCategory[getCacheKey(activeTab, mediaFilter)]?.length >= 2 && onCreateWithAI && (
          <CreateWithAICard onClick={onCreateWithAI} />
        )}
        
        {/* Empty state when filter has no results */}
        {activeTab && !paginationState[getCacheKey(activeTab, mediaFilter)]?.loading && templatesByCategory[getCacheKey(activeTab, mediaFilter)]?.length === 0 && (
          <div className="col-span-full text-center py-12">
            <div className="text-gray-400 mb-4">
              {mediaFilter === "video" ? (
                <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              ) : (
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              )}
            </div>
            <h3 className="text-lg font-medium text-gray-600 mb-2">
              No {mediaFilter === "video" ? "video" : "image"} templates in this category
            </h3>
            <p className="text-gray-500 text-sm mb-4">
              Try selecting "{mediaFilter === "video" ? "Images" : "Videos"}" or "All Templates" to see more options.
            </p>
            <button
              onClick={() => setMediaFilter("all")}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Show All Templates
            </button>
          </div>
        )}
      </div>

      {/* Load More button */}
      {activeTab && paginationState[getCacheKey(activeTab, mediaFilter)]?.hasMore && !paginationState[getCacheKey(activeTab, mediaFilter)]?.loading && templatesByCategory[getCacheKey(activeTab, mediaFilter)]?.length > 0 && (
        <div className="mt-8 flex flex-col items-center gap-2">
          <button
            onClick={handleLoadMore}
            disabled={paginationState[getCacheKey(activeTab, mediaFilter)]?.loading}
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            Load More Templates
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <span className="text-sm text-gray-500">
            Showing {templatesByCategory[getCacheKey(activeTab, mediaFilter)]?.length || 0} of {paginationState[getCacheKey(activeTab, mediaFilter)]?.total || 0}
          </span>
        </div>
      )}
      
      {/* Load More loading state */}
      {activeTab && paginationState[activeTab]?.loading && templatesByCategory[activeTab]?.length > 0 && (
        <div className="mt-8 flex flex-col items-center gap-2">
          <div className="flex items-center gap-3">
            <LoadingSpinner size="sm" />
            <span className="text-gray-600 font-medium">Loading more templates...</span>
          </div>
        </div>
      )}

      {/* Empty state when no templates and not loading */}
      {activeTab && !paginationState[activeTab]?.loading && !templatesByCategory[activeTab]?.length && !categoriesLoading && (
        <div className="flex flex-col justify-center items-center py-12 text-gray-500">
          <svg className="w-12 h-12 mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-sm">No templates found for this category</p>
        </div>
      )}
    </div>
  );
}
